import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import type { ThemePreference } from '../utils/theme';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

const apiUrl = (path: string) => `${API_BASE_URL}${path}`;

interface User {
  id: string;
  email: string;
  preferences?: {
    theme?: ThemePreference;
    language?: string;
  };
  subscription?: {
    status?: string;
    tier?: string;
  };
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  updateProfile: (updates: { preferences?: { language?: string } }) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetch(apiUrl('/api/auth/me'), { credentials: 'include' })
      .then(async (response) => {
        if (!response.ok) return null;
        const payload = await response.json();
        return payload.user as User;
      })
      .then((nextUser) => {
        if (!cancelled) setUser(nextUser);
      })
      .catch((error) => {
        console.error('Failed to load user session:', error);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const updateProfile = useCallback(async (updates: { preferences?: { language?: string } }) => {
    const response = await fetch(apiUrl('/api/auth/profile'), {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    });
    if (!response.ok) throw new Error('Failed to update profile');
    const payload = await response.json();
    setUser(payload.user as User);
  }, []);

  return <AuthContext.Provider value={{ user, loading, updateProfile }}>{children}</AuthContext.Provider>;
}